'use client';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';

import CountrySelect from '../CountrySelect';
import Input from '../Input';
import TextArea from '../Textarea';
import {
  IContactPageDictionary,
  IContactPageFormDictionary,
} from '@/dictionaries/pages/contacts/types';
import { getTranslatedSchema } from '../ContactForm/schema';
import ImageSelector from '../ImageSelector';
import { useState } from 'react';
import Button from '../Button';
import { cn } from '@/lib/utils';
import { postApplyNow, postMedia } from '@/actions/applyNow';
import useCountries from '@/hooks/useCountries';
import SuccessModal from '@/components/SuccessModal';
import { LuLoader2 } from 'react-icons/lu';
import { useLocale } from '@/hooks/useLocale';

type OwnersFormProps = {
  formDict: IContactPageFormDictionary;
  countriesDict: ICountriesDictionary;
  contactDict: IContactPageDictionary;
};

declare const window: Window & { dataLayer: Record<string, unknown>[] };

export default function OwnersForm({
  formDict,
  countriesDict,
  contactDict,
}: OwnersFormProps) {
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lang = useLocale();

  const schema = getTranslatedSchema(formDict.errors);

  const { translatedCountries, languageCountry } = useCountries({
    lang,
    countriesDict,
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      message: 'some message',
      country_info: {
        name: languageCountry?.name,
        code: languageCountry?.code,
        id: languageCountry?.id,
        phone: {
          code: languageCountry?.id,
          country: languageCountry?.name,
        },
      },
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    setIsSubmitting(true);

    window.dataLayer = window.dataLayer || [];

    const body = {
      firstName: data.name,
      lastName: data.surname,
      email: data.email,
      phone: data.phone,
      files: [{}],
      address: {
        city: data.town_country,
        postalCode: data.post_code,
        street: data.villa_address,
        country: data.country_info.id,
      },
      options: {
        website: data.website,
        description: data.property_description,
      },
      phoneCode: data.country_info.phone.code,
      privacyPolicy: true,
      type: 'owner_request',
    };

    const response = await postApplyNow(body, lang);
    if (response) {
      setSuccessModalIsOpen(true);

      window.dataLayer.push({
        event: 'eventContactOwner',
        eventCategory: 'send_contact',
        eventAction: 'form_owner',
        eventLabel: window.location.href,
        language: lang,
      });

      if (uploadedImages.length > 0) {
        const formData = new FormData();

        formData.append('lead', response.id);

        uploadedImages.map((file, index) => {
          formData.append(`files[${index}]`, file);
        });

        await postMedia(formData);
      }
      reset();
    }

    setIsSubmitting(false);
  });

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      {successModalIsOpen && (
        <SuccessModal
          modalIsOpen={successModalIsOpen}
          setModalIsOpen={setSuccessModalIsOpen}
          title={contactDict.successModal.title}
          description={contactDict.successModal.description}
        />
      )}
      <div className="flex flex-col gap-4 md:gap-0 md:flex-row md:justify-between">
        <Input.Container className="md:w-[calc(50%-1rem)]">
          <Input.Label htmlFor="name">{formDict.name}</Input.Label>
          <Input.Root className="bg-white" register={register} name="name" />
          <Input.Error error={errors.name} />
        </Input.Container>

        <Input.Container className="md:w-[calc(50%-1rem)]">
          <Input.Label htmlFor="surname">{formDict.surname}</Input.Label>
          <Input.Root className="bg-white" register={register} name="surname" />
          <Input.Error error={errors.surname} />
        </Input.Container>
      </div>

      <div className="flex flex-col gap-4 md:gap-0 md:flex-row md:justify-between">
        <Input.Container className="md:w-[calc(50%-1rem)]">
          <Input.Label htmlFor="email">{formDict.email}</Input.Label>
          <Input.Root className="bg-white" register={register} name="email" />
          <Input.Error error={errors.email} />
        </Input.Container>

        <div className="flex flex-col md:w-[calc(50%-1rem)]">
          <label
            className="text-sm text-gray-700 leading-[1.8] w-fit"
            htmlFor="phone-country-selector">
            {formDict.phone}
          </label>
          <div className="flex flex-row">
            <div className="w-[250px] md:w-[280px] xl:w-[200px] h-fit">
              <CountrySelect
                buttonClassName="!bg-white"
                countries={translatedCountries}
                label=""
                id={'phone-country-selector'}
                showPhoneCode
                onChange={({ id, name }) => {
                  setValue('country_info.phone.code', id);
                  setValue('country_info.phone.country', name);
                }}
                selectedValue={translatedCountries.find(
                  (option) =>
                    option.name === watch('country_info.phone.country'),
                )}
              />
            </div>
            <Input.Container className="w-full">
              <Input.Root
                className="bg-white"
                register={register}
                name="phone"
              />
              <Input.Error error={errors.phone} />
            </Input.Container>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:gap-0 md:flex-row md:justify-between">
        <Input.Container className="md:w-[calc(50%-1rem)]">
          <Input.Label htmlFor="villa_address">
            {formDict.villaAddress}
          </Input.Label>
          <Input.Root
            className="bg-white"
            register={register}
            name="villa_address"
          />
          <Input.Error error={errors.villa_address} />
        </Input.Container>

        <Input.Container className="md:w-[calc(50%-1rem)]">
          <Input.Label htmlFor="town_country">
            {formDict.townCountry}
          </Input.Label>
          <Input.Root
            className="bg-white"
            register={register}
            name="town_country"
          />
        </Input.Container>
      </div>
      <div className="flex flex-col gap-4 md:gap-0 md:flex-row md:justify-between">
        <Input.Container className="md:w-[calc(50%-1rem)]">
          <Input.Label htmlFor="post_code">{formDict.postCode}</Input.Label>
          <Input.Root
            className="bg-white"
            register={register}
            name="post_code"
          />
          <Input.Error error={errors.post_code} />
        </Input.Container>

        <div className="md:w-[calc(50%-1rem)] h-fit">
          <CountrySelect
            buttonClassName="bg-white"
            countries={translatedCountries}
            label={formDict.country}
            id={'country-selector'}
            onChange={({ code, name, id }) => {
              setValue('country_info.name', name);
              setValue('country_info.code', code);
              setValue('country_info.id', id);
            }}
            selectedValue={translatedCountries.find(
              (option) => option.name === watch('country_info.name'),
            )}
          />
        </div>
      </div>

      <Input.Container className="">
        <Input.Label htmlFor="website">
          {formDict.website} <i>{formDict.optional}</i>
        </Input.Label>
        <Input.Root className="bg-white" register={register} name="website" />
      </Input.Container>

      <TextArea
        className=""
        inputClassName="bg-white"
        register={register}
        name="property_description"
        error={errors.property_description}
        label={formDict.propertyDesc}
      />

      <div className="flex flex-col w-full">
        <label className="text-sm text-footer-gray">
          {formDict.upload}
          <i className="ml-2">(Max. 2MB)</i>
        </label>
        <ImageSelector
          className="md:!w-1/2"
          files={uploadedImages}
          removeFile={(targetFile) => {
            setUploadedImages(
              uploadedImages.filter((file) => file != targetFile),
            );
          }}
          onChange={(file) => {
            setUploadedImages((prev) => [...prev, file]);
          }}>
          {formDict.addPhotos}
        </ImageSelector>
      </div>

      <div className="w-full flex flex-col gap-4 md:gap-0 items-start md:flex-row md:items-center !mt-12">
        <div className="flex flex-col w-full h-fit md:w-1/2">
          <div className="flex items-start gap-[1rem]">
            <input
              className={cn(
                'bg-white border-2 border-[#e6e6e6] cursor-pointer w-6 h-[24px] appearance-none m-0 grid place-content-center [&]:before:content-[""] [&]:before:w-4 [&]:before:h-[16px] [&]:before:scale-0 [&]:before:bg-underline [&]:checked:before:scale-100',
                {
                  'border border-underline': watch('terms') == true,
                },
              )}
              {...register('terms')}
              onChange={(e) => setValue('terms', e.target.checked)}
              type="checkbox"
              name="checkbox-input"
            />
            <label
              className="text-sm text-footer-gray"
              htmlFor="checkbox-input">
              {formDict.terms}
              <span className="hover:text-gray-700 underline cursor-pointer ml-1 text-footer-gray">
                {formDict.privacyPolicy}
              </span>
            </label>
          </div>
          <Input.Error error={errors.terms} />
        </div>

        <div className="w-full md:w-1/2 ml-auto flex items-end">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex gap-2">
            {isSubmitting ? (
              <LuLoader2 className="animate-spin" />
            ) : (
              formDict.sendRequest
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
